import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  ClockInJobsResponse,
  ClockPunchInput,
  ClockPunchResponse,
} from './contractsMirror.js';

/**
 * Self-clock (punch) hooks. Every punch endpoint resolves the acting employee from the SESSION
 * server-side (`loadEmployeeForUser`) — the SPA never sends an employee id, so one user can never
 * punch for another. The SERVER also owns all hour + lunch arithmetic; nothing here computes time.
 *
 * `useClockStateQuery` returns the job list AND the caller's own open shift, so the card can render
 * the single action that is actually available rather than offering four and letting the server
 * reject three.
 */

/** The clock-in job list plus the caller's own open shift. */
export function useClockStateQuery(enabled: boolean) {
  return useQuery<ClockInJobsResponse>({
    queryKey: ['timesheets', 'clock-state'],
    queryFn: () => apiFetchJson<ClockInJobsResponse>('/api/v1/timesheets/clock-in-jobs'),
    enabled,
  });
}

/** Invalidate both the clock card and the week grid — a punch changes each of them. */
function useRefreshAfterPunch() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
  };
}

/**
 * How long a punch may hang before we call it failed.
 *
 * A PUNCH MUST BE ABLE TO FAIL. `fetch` has no timeout of its own and react-query does not add one,
 * so on a stalled connection the request simply never settles — and `isPending` never goes back to
 * false. The clock card disables the Clock in button AND the site field while a punch is pending,
 * so a crew member in a dead zone is left with a greyed-out screen, no error, and nothing to do
 * but reload the page. Hanging forever is not safer than failing: the punch had not been recorded
 * either way, and only one of those two states lets somebody try again.
 *
 * Twenty seconds is well past a slow-but-working rural connection and well short of the patience of
 * somebody standing on a roof.
 */
const PUNCH_TIMEOUT_MS = 20_000;

/**
 * One punch. `path` is the endpoint suffix (`clock-in`, `clock-out`).
 * `punchedAt` is deliberately NOT sent from the browser: the dashboard is online by definition, so
 * the server's clock is the honest one. (The mobile app sends it because a queued punch from a dead
 * zone must book the time it was taken.)
 *
 * The timeout covers the WHOLE operation, including the single-flight token refresh `apiFetch` may
 * do on a 401. That is deliberate — it is the budget from the crew member's side of the screen, not
 * per network attempt.
 */
function usePunchMutation(path: string) {
  const refresh = useRefreshAfterPunch();
  return useMutation<ClockPunchResponse, Error, ClockPunchInput | void>({
    mutationFn: (input) =>
      apiFetchJson<ClockPunchResponse>(`/api/v1/timesheets/${path}`, {
        method: 'POST',
        body: JSON.stringify(input ?? {}),
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(PUNCH_TIMEOUT_MS),
      }),
    onSuccess: refresh,
  });
}

export const useClockInMutation = () => usePunchMutation('clock-in');
export const useClockOutMutation = () => usePunchMutation('clock-out');

// The lunch-start / lunch-end mutations were removed 2026-07-28 with the buttons that called them.
// The ENDPOINTS still exist and still accept punches — the phone app's offline queue replays lunch
// punches written to disk by older builds, and refusing those would fail a punch somebody really
// took. Nothing in this dashboard should call them again: the deduction is decided by the length of
// the shift, so a lunch punch can no longer change what anyone is paid.
